import React from 'react';
import { formatCurrency, formatPercent, formatMoney, signClass } from '../../hooks/useResultsAnalysis';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';

const PIE_COLORS = ['#00ff41', '#00d4ff', '#ffb000', '#ff073a', '#00ff41', '#00d4ff', '#ffb000'];

function LtrAnalysis({ analysis }) {
  const {
    purchasePrice, downPayment, loanAmount, monthlyPayment,
    renovationCost, effectiveAnnualRent, propertyTaxAnnual,
    insuranceAnnual, maintenanceAnnual, utilitiesAnnual,
    capexAnnual, propertyManagementFee, totalAnnualExpenses,
    annualCashFlow, ltrCashOnCashReturn, monthlyRent, vacancyRate,
    loanPaydown, closingCosts, initialInvestment, managementFeePercent,
    annualDebtService, interestRate, loanTermYears, downPaymentPercent,
    ltrFiveYearCashFlow, renovationMonths, propertyTaxEstimated, insuranceEstimated, forcedEquity, marketAppreciation, exitValueBasis, annualAppreciationPercent,
    futureSellingCosts, sellingCostPercent, rentalProfit, ltrTotalROIAnnualized,
    hoaAnnual,
  } = analysis;

  const expenseData = [
    { name: 'Property Tax', value: propertyTaxAnnual },
    { name: 'Insurance', value: insuranceAnnual },
    ...(hoaAnnual > 0 ? [{ name: 'HOA', value: hoaAnnual }] : []),
    { name: 'Maintenance', value: maintenanceAnnual },
    { name: 'Utilities', value: utilitiesAnnual },
    { name: 'CapEx', value: capexAnnual },
    { name: 'Management', value: propertyManagementFee },
    { name: 'Mortgage', value: annualDebtService },
  ];

  return (
    <div className="mb-8 border border-surface-border rounded bg-surface-primary overflow-hidden">
      <div className="bg-surface-elevated border-b border-surface-border px-6 py-4">
        <h2 className="text-xl font-semibold text-terminal-green">Long-Term Rental Strategy Detailed Analysis</h2>
        <p className="text-sm text-terminal-green">Rent, costs and returns over five years</p>
      </div>

      <div className="p-6">
        {/* Initial Investment & Monthly Cash Flow */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Initial Investment */}
          <div className="bg-surface-primary border rounded p-5">
            <h3 className="text-lg font-medium text-txt-primary mb-4">Initial Investment</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center pb-2 border-b border-surface-border">
                <span className="text-txt-secondary">Purchase Price</span>
                <span className="font-medium">${formatCurrency(purchasePrice)}</span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b border-surface-border">
                <span className="text-txt-secondary">Down Payment ({downPaymentPercent}%)</span>
                <span className="font-medium">${formatCurrency(downPayment)}</span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b border-surface-border">
                <span className="text-txt-secondary">Loan Amount</span>
                <span className="font-medium">${formatCurrency(loanAmount)}</span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b border-surface-border">
                <span className="text-txt-secondary">Interest Rate</span>
                <span className="font-medium">{interestRate}%</span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b border-surface-border">
                <span className="text-txt-secondary">Loan Term</span>
                <span className="font-medium">{loanTermYears} years</span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b border-surface-border">
                <span className="text-txt-secondary">Monthly Payment</span>
                <span className="font-medium">${formatCurrency(monthlyPayment)}</span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b border-surface-border">
                <span className="text-txt-secondary">Renovation Cost</span>
                <span className="font-medium">${formatCurrency(renovationCost)}</span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b border-surface-border">
                <span className="text-txt-secondary">Closing Costs</span>
                <span className="font-medium">${formatCurrency(closingCosts)}</span>
              </div>
              <div className="flex justify-between items-center font-medium text-txt-primary">
                <span>Total Initial Investment</span>
                <span>${formatCurrency(initialInvestment)}</span>
              </div>
            </div>
          </div>

          {/* Monthly Cash Flow */}
          <div className="bg-surface-primary border rounded p-5">
            <h3 className="text-lg font-medium text-txt-primary mb-4">Monthly Cash Flow</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center pb-2 border-b border-surface-border">
                <span className="text-txt-secondary">Monthly Rent (effective)</span>
                <span className="font-medium">${formatCurrency(effectiveAnnualRent / 12)}</span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b border-surface-border">
                <span className="text-txt-secondary">Mortgage Payment</span>
                <span className="font-medium">-${formatCurrency(monthlyPayment)}</span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b border-surface-border">
                <span className="text-txt-secondary">Property Tax</span>
                <span className="font-medium">-${formatCurrency(propertyTaxAnnual / 12)}</span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b border-surface-border">
                <span className="text-txt-secondary">Insurance</span>
                <span className="font-medium">-${formatCurrency(insuranceAnnual / 12)}</span>
              </div>
              {hoaAnnual > 0 && (
                <div className="flex justify-between items-center pb-2 border-b border-surface-border">
                  <span className="text-txt-secondary">HOA Dues</span>
                  <span className="font-medium">-${formatCurrency(hoaAnnual / 12)}</span>
                </div>
              )}
              <div className="flex justify-between items-center pb-2 border-b border-surface-border">
                <span className="text-txt-secondary">Maintenance</span>
                <span className="font-medium">-${formatCurrency(maintenanceAnnual / 12)}</span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b border-surface-border">
                <span className="text-txt-secondary">Utilities</span>
                <span className="font-medium">-${formatCurrency(utilitiesAnnual / 12)}</span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b border-surface-border">
                <span className="text-txt-secondary">Management Fee ({managementFeePercent}%)</span>
                <span className="font-medium">-${formatCurrency(propertyManagementFee / 12)}</span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b border-surface-border">
                <span className="text-txt-secondary">Capital Reserves</span>
                <span className="font-medium">-${formatCurrency(capexAnnual / 12)}</span>
              </div>
              <div className={`flex justify-between items-center font-medium ${signClass(annualCashFlow)}`}>
                <span>Monthly Cash Flow</span>
                <span>{formatMoney(annualCashFlow / 12)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Annual Revenue and Expense Breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Annual Revenue Breakdown */}
          <div className="bg-surface-primary border rounded p-5">
            <h3 className="text-lg font-medium text-txt-primary mb-4">Annual Revenue Breakdown</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center pb-2 border-b border-surface-border">
                <span className="text-txt-secondary">Monthly Rent</span>
                <span className="font-medium">${formatCurrency(monthlyRent)}</span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b border-surface-border">
                <span className="text-txt-secondary">Vacancy Rate</span>
                <span className="font-medium">{vacancyRate}%</span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b border-surface-border">
                <span className="text-txt-secondary">Effective Annual Rent</span>
                <span className="font-medium">${formatCurrency(effectiveAnnualRent)}</span>
              </div>
              <div className="flex justify-between items-center font-medium text-terminal-green pt-2 border-t border-surface-border">
                <span>Total Annual Revenue</span>
                <span>${formatCurrency(effectiveAnnualRent)}</span>
              </div>
            </div>

            <div className="mt-6">
              <h4 className="text-sm font-medium text-txt-primary mb-2">Occupancy Distribution</h4>
              <div className="flex h-8 rounded overflow-hidden">
                <div
                  className="bg-terminal-green"
                  style={{ width: `${100 - vacancyRate}%` }}
                  title={`Occupied: ${100 - vacancyRate}%`}
                ></div>
                <div
                  className="bg-surface-elevated"
                  style={{ width: `${vacancyRate}%` }}
                  title={`Vacant: ${vacancyRate}%`}
                ></div>
              </div>
              <div className="flex justify-between text-xs mt-1">
                <span className="text-terminal-green">Occupied: {Math.round(365 * (100 - vacancyRate) / 100)} days</span>
                <span className="text-txt-muted">Vacant: {Math.round(365 * vacancyRate / 100)} days</span>
              </div>
            </div>
          </div>

          {/* Annual Expense Breakdown */}
          <div className="bg-surface-primary border rounded p-5">
            <h3 className="text-lg font-medium text-txt-primary mb-4">Annual Expense Breakdown</h3>
            <div className="space-y-2">
              <div className="flex justify-between items-center pb-2 border-b border-surface-border">
                <span className="text-txt-secondary">Property Tax</span>
                <span className="font-medium">-${formatCurrency(propertyTaxAnnual)}</span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b border-surface-border">
                <span className="text-txt-secondary">Insurance</span>
                <span className="font-medium">-${formatCurrency(insuranceAnnual)}</span>
              </div>
              {hoaAnnual > 0 && (
                <div className="flex justify-between items-center pb-2 border-b border-surface-border">
                  <span className="text-txt-secondary">HOA Dues</span>
                  <span className="font-medium">-${formatCurrency(hoaAnnual)}</span>
                </div>
              )}
              <div className="flex justify-between items-center pb-2 border-b border-surface-border">
                <span className="text-txt-secondary">Maintenance</span>
                <span className="font-medium">-${formatCurrency(maintenanceAnnual)}</span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b border-surface-border">
                <span className="text-txt-secondary">Utilities</span>
                <span className="font-medium">-${formatCurrency(utilitiesAnnual)}</span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b border-surface-border">
                <span className="text-txt-secondary">Capital Expenditures</span>
                <span className="font-medium">-${formatCurrency(capexAnnual)}</span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b border-surface-border">
                <span className="text-txt-secondary">Management Fee ({managementFeePercent}%)</span>
                <span className="font-medium">-${formatCurrency(propertyManagementFee)}</span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b border-surface-border">
                <span className="text-txt-secondary">Mortgage Payments</span>
                <span className="font-medium">-${formatCurrency(annualDebtService)}</span>
              </div>
              <div className="flex justify-between items-center font-medium text-terminal-red">
                <span>Total Annual Expenses</span>
                <span>-${formatCurrency(totalAnnualExpenses + annualDebtService)}</span>
              </div>
            </div>

            <div className="mt-4">
              <div className={`flex justify-between items-center font-medium pt-2 border-t border-surface-border ${signClass(annualCashFlow)}`}>
                <span>Annual Net Cash Flow</span>
                <span>{formatMoney(annualCashFlow)}</span>
              </div>
              <div className="flex justify-between items-center text-sm text-txt-secondary pt-1">
                <span>Monthly Average</span>
                <span>{formatMoney(annualCashFlow / 12)}/month</span>
              </div>
            </div>
          </div>
        </div>

        {/* LTR Expense Distribution Chart */}
        <div className="bg-surface-primary border rounded p-5 mb-6">
          <h3 className="text-lg font-medium text-txt-primary mb-4">LTR Expense Distribution</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={expenseData}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  fill="#00ff41"
                  dataKey="value"
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                >
                  {PIE_COLORS.map((color, index) => (
                    <Cell key={`cell-${index}`} fill={color} />
                  ))}
                </Pie>
                <RechartsTooltip
                  separator=""
                  offset={24}
                  formatter={(value) => [`$${formatCurrency(value)}`, '']}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>


        <p className="text-sm text-txt-secondary mb-6">
          {[
            propertyTaxEstimated && 'Property tax is estimated at 1.2% of the purchase price',
            insuranceEstimated && 'insurance at 0.5%',
          ].filter(Boolean).join(' and ')}
          {(propertyTaxEstimated || insuranceEstimated) ? ' because none was entered; add your own figures in the calculator. ' : ''}
          Other estimates: maintenance 1% and owner-paid utilities 0.5% of the price a year, and reserves of 5% of rent.
        </p>

        {/* Key Performance Metrics */}
        <div className="bg-surface-primary border rounded p-5 mb-6">
          <h3 className="text-lg font-medium text-txt-primary mb-4">Key Performance Metrics</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-surface-elevated rounded p-4 text-center">
              <p className="text-sm text-terminal-amber mb-1">Cash Flow</p>
              <p className={`text-2xl font-bold ${signClass(annualCashFlow)}`}>{formatMoney(annualCashFlow / 12)}/mo</p>
              <p className={`text-sm ${signClass(annualCashFlow)}`}>{formatMoney(annualCashFlow)}/yr</p>
            </div>
            <div className="bg-surface-elevated rounded p-4 text-center">
              <p className="text-sm text-terminal-green mb-1">Cash-on-Cash Return</p>
              <p className={`text-2xl font-bold ${signClass(ltrCashOnCashReturn)}`}>{formatPercent(ltrCashOnCashReturn)}%</p>
              <p className="text-sm text-terminal-green">A full rented year of cash flow / cash invested</p>
            </div>
            <div className="bg-surface-elevated rounded p-4 text-center">
              <p className="text-sm text-terminal-cyan mb-1">Vacancy Rate</p>
              <p className="text-2xl font-bold text-terminal-cyan">{vacancyRate}%</p>
              <p className="text-sm text-terminal-cyan">{Math.round(365 * vacancyRate / 100)} days/year</p>
            </div>
            <div className="bg-surface-elevated rounded p-4 text-center">
              <p className="text-sm text-terminal-amber mb-1">Monthly Rent</p>
              <p className="text-2xl font-bold text-terminal-amber">${formatCurrency(monthlyRent)}</p>
              <p className="text-sm text-terminal-amber">gross monthly</p>
            </div>
          </div>
        </div>

        {/* 5-Year Return Projection */}
        <div className="bg-surface-primary border rounded p-5 mb-6">
          <h3 className="text-lg font-medium text-txt-primary mb-4">5-Year Return Projection</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-surface-border">
              <thead className="bg-surface-elevated">
                <tr>
                  <th scope="col" className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-txt-muted uppercase tracking-wider">Component</th>
                  <th scope="col" className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-txt-muted uppercase tracking-wider">Amount</th>
                  <th scope="col" className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-txt-muted uppercase tracking-wider">Calculation</th>
                </tr>
              </thead>
              <tbody className="bg-surface-primary divide-y divide-surface-border">
                <tr>
                  <td className="px-4 sm:px-6 py-4 text-sm text-txt-primary">Cash Flow (5 years)</td>
                  <td className={`px-4 sm:px-6 py-4 whitespace-nowrap text-sm font-medium ${signClass(ltrFiveYearCashFlow)}`}>{formatMoney(ltrFiveYearCashFlow)}</td>
                  <td className="px-4 sm:px-6 py-4 text-sm text-txt-muted">Sum of years 1-5 with rent and expense growth{renovationMonths > 0 ? `; no rent for the first ${renovationMonths} months while renovating` : ''}</td>
                </tr>
                <tr>
                  <td className="px-4 sm:px-6 py-4 text-sm text-txt-primary">Value Created by Renovation</td>
                  <td className={`px-4 sm:px-6 py-4 whitespace-nowrap text-sm font-medium ${signClass(forcedEquity)}`}>{formatMoney(forcedEquity)}</td>
                  <td className="px-4 sm:px-6 py-4 text-sm text-txt-muted">Starting value ${formatCurrency(exitValueBasis)} less purchase price and renovation cost</td>
                </tr>
                <tr>
                  <td className="px-4 sm:px-6 py-4 text-sm text-txt-primary">Market Appreciation ({annualAppreciationPercent}% annually)</td>
                  <td className={`px-4 sm:px-6 py-4 whitespace-nowrap text-sm font-medium ${signClass(marketAppreciation)}`}>{formatMoney(marketAppreciation)}</td>
                  <td className="px-4 sm:px-6 py-4 text-sm text-txt-muted">An assumption about future prices, not a forecast</td>
                </tr>
                <tr>
                  <td className="px-4 sm:px-6 py-4 text-sm text-txt-primary">Loan Principal Paydown</td>
                  <td className={`px-4 sm:px-6 py-4 whitespace-nowrap text-sm font-medium ${signClass(loanPaydown)}`}>{formatMoney(loanPaydown)}</td>
                  <td className="px-4 sm:px-6 py-4 text-sm text-txt-muted">Reduction in loan balance over 5 years</td>
                </tr>
                <tr>
                  <td className="px-4 sm:px-6 py-4 text-sm text-txt-primary">Purchase Closing Costs</td>
                  <td className={`px-4 sm:px-6 py-4 whitespace-nowrap text-sm font-medium ${'text-terminal-red'}`}>{formatMoney(-closingCosts)}</td>
                  <td className="px-4 sm:px-6 py-4 text-sm text-txt-muted">Paid up front</td>
                </tr>
                <tr>
                  <td className="px-4 sm:px-6 py-4 text-sm text-txt-primary">Selling Costs at Exit ({sellingCostPercent}%)</td>
                  <td className={`px-4 sm:px-6 py-4 whitespace-nowrap text-sm font-medium ${'text-terminal-red'}`}>{formatMoney(-futureSellingCosts)}</td>
                  <td className="px-4 sm:px-6 py-4 text-sm text-txt-muted">On the year-5 sale value</td>
                </tr>
                <tr className="bg-surface-elevated">
                  <td className="px-4 sm:px-6 py-4 text-sm font-medium text-txt-primary">Net 5-Year Profit</td>
                  <td className={`px-4 sm:px-6 py-4 whitespace-nowrap text-sm font-bold ${signClass(rentalProfit)}`}>{formatMoney(rentalProfit)}</td>
                  <td className="px-4 sm:px-6 py-4 text-sm text-txt-muted">Sum of all components</td>
                </tr>
                <tr className="bg-surface-elevated">
                  <td className="px-4 sm:px-6 py-4 text-sm font-medium text-txt-primary">Total ROI (Annualized)</td>
                  <td className={`px-4 sm:px-6 py-4 whitespace-nowrap text-sm font-bold ${signClass(ltrTotalROIAnnualized)}`}>{formatPercent(ltrTotalROIAnnualized)}%</td>
                  <td className="px-4 sm:px-6 py-4 text-sm text-txt-muted">Compound annual return on ${formatCurrency(initialInvestment)} invested</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LtrAnalysis;
